import React from 'react';
import { TONE } from './rmaHelpers';

// Small shared pieces used by more than one RMA screen. Kept together rather than one file
// each — they are a few lines apiece and always travel together.

// Device-type chip. The design used a fixed indigo, which turned out to be the --dtype-2-*
// token trio; hashing the type name means two device types are distinguishable instead of
// both being indigo.
//
// Slots 1 (green) and 5 (red) are excluded on purpose — in a screen full of pass/fail and
// warranty colouring, a green or red chip would read as a status rather than a type.
const DTYPE_SLOTS = [2, 3, 4, 6];
export function DeviceTypeChip({ type, style }) {
  if (!type) return null;
  let h = 0;
  for (let i = 0; i < type.length; i++) h = (h * 31 + type.charCodeAt(i)) | 0;
  const slot = DTYPE_SLOTS[Math.abs(h) % DTYPE_SLOTS.length];
  return (
    <span
      className="chip"
      style={{
        background: `var(--dtype-${slot}-fill, var(--surface-inset))`,
        color: `var(--dtype-${slot}-text, var(--text-muted))`,
        border: `1px solid var(--dtype-${slot}-border, var(--border-subtle))`,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {type}
    </span>
  );
}

// Tinted callout box — the SLA warning and the chargeable-unit notice on the detail screen.
export function Callout({ tone = 'info', title, children, style }) {
  const t = TONE[tone] || TONE.info;
  return (
    <div style={{ background: t.fill, border: '1px solid ' + t.border, borderRadius: 'var(--radius-lg)', padding: '11px 12px', ...style }}>
      {title && <div style={{ fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 3 }}>{title}</div>}
      {children && <div style={{ fontSize: 11.5, color: t.text, lineHeight: 1.45 }}>{children}</div>}
    </div>
  );
}

// Right-aligned key/value row used by the Shipment and Summary cards.
export function FactRow({ label, value, mono, valueColor, last }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12,
      padding: '7px 0', borderBottom: last ? 'none' : '1px solid var(--border-subtle)',
    }}>
      <span style={{ fontSize: 12, color: 'var(--text-subtle)', flexShrink: 0 }}>{label}</span>
      <span style={{
        fontSize: 12.5, fontWeight: 600, textAlign: 'right',
        color: valueColor || 'var(--text-default)',
        fontFamily: mono ? 'var(--font-mono)' : 'inherit',
        wordBreak: 'break-word',
      }}>{value}</span>
    </div>
  );
}

// Serial in the app's mono face. Used everywhere a serial or RMA ref is shown.
export function Mono({ children, size = 12.5, weight = 600, color = 'var(--text-primary)', style }) {
  return (
    <span style={{ fontFamily: 'var(--font-mono)', fontSize: size, fontWeight: weight, color, ...style }}>
      {children}
    </span>
  );
}

// Column header for the grid "tables".
export function Th({ children, align }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px',
      color: 'var(--text-subtle)', textAlign: align || 'left',
    }}>{children}</div>
  );
}

// Empty state shared by the queue, the staging lists and the scan tables.
export function EmptyState({ icon, title, children }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
      gap: 10, padding: '40px 24px',
    }}>
      {icon && (
        <div style={{
          width: 40, height: 40, background: 'var(--surface-inset)', border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-lg)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--text-ghost)',
        }}>{icon}</div>
      )}
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{title}</div>
      {children && (
        <div style={{ fontSize: 12.5, color: 'var(--text-subtle)', maxWidth: 340, lineHeight: 1.5 }}>{children}</div>
      )}
    </div>
  );
}
